import { IcIcon } from "@shoutout-labs/shoutout-themes-enterprise";
import {
    faStatistics,
    faMedal,
    faShoppingCart,
    faUserSquare,
} from "FaICIconMap";
import { toTitleCase } from "Utils";

const CampaignChannels = {
    SMS: "SMS",
    EMAIL: "EMAIL",
    BANNER: "BANNER",
};

const CampaignAction = {
    CREATE_CAMPAIGN: "CREATE_CAMPAIGN",
    EDIT_CAMPAIGN: "EDIT_CAMPAIGN",
};

const CampaignTypes = {
    PROMOTIONAL: "PROMOTIONAL",
    TRANSACTIONAL: "TRANSACTIONAL",
};

const CampaignSubTypes = {
    GOAL_DRIVEN: "GOAL_DRIVEN",
    QUICK: "QUICK",
    ALL: "ALL",
};

const CampaignTypeOptions = Object.values(CampaignTypes).map((cT) => ({
    label: toTitleCase(cT),
    value: cT,
}));

const CampaignSubTypesInfo = {
    [CampaignSubTypes.GOAL_DRIVEN]:
        "- Send campaigns with goals, track results and view reports",
    [CampaignSubTypes.QUICK]: "- Send quick updates, greetings or promotions",
};

const CampaignSubTypesTabs = {
    ...CampaignSubTypes,
    ALL: "ALL",
};

const GoalDrivenCampaignsStatus = {
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE",
};

const GoalDrivenCampaigns = [
    {
        goalId: "SALES_UPLIFT",
        icon: <IcIcon className="flip-vert" size="lg" icon={faStatistics} />,
        goal: "Sales Uplift",
        goapSubHeading: "Increase revenue and purchases",
        status: GoalDrivenCampaignsStatus.INACTIVE,
        availableReports: [
            "ROI",
            "Total Sales Revenue",
            "Total Sales Count",
            "Conversion Rate",
            "Avg. Spend Per Member",
            "Sales Trend",
        ],
    },
    {
        goalId: "POINTS_COLLECTION",
        icon: <IcIcon size="lg" icon={faMedal} />,
        goal: "Points Collection",
        goapSubHeading: "Increase points earned by members",
        status: GoalDrivenCampaignsStatus.ACTIVE,
        availableReports: [
            "ROI",
            "Total Points Collection Transactions",
            "Conversion Rate",
            "Total Collected Points",
            "Avg. Points Earned Per Member",
            "Points Collection Trend",
        ],
    },
    {
        goalId: "REDEMPTION_VOLUME",
        icon: <IcIcon size="lg" icon={faShoppingCart} />,
        goal: "Redemption Volume",
        goapSubHeading: "Increase points redemption by member",
        status: GoalDrivenCampaignsStatus.INACTIVE,
        availableReports: [
            "ROI",
            "Total Redeemed Points",
            "Total Points Redemption Transactions",
            "Avg. Points Redeemed Per Member",
            "Conversion Rate",
            "Points Redemption Trend",
        ],
    },
    {
        goalId: "CUSTOMER_REACTIVATION",
        icon: <IcIcon size="lg" icon={faUserSquare} />,
        goal: "Customer Reactivation",
        goapSubHeading: "Re-engage dormant customers",
        status: GoalDrivenCampaignsStatus.INACTIVE,
        availableReports: [
            "ROI",
            "Reactivated Members",
            "Transactions From Reactivated Members",
            "Avg. Spend Per Reactivated Member",
            "Conversion Rate",
            "Member Reactivation Trend",
        ],
    },
];

const CampaignStatus = {
    CREATING: "CREATING",
    RUNNING: "RUNNING",
    FINISHED: "FINISHED",
    LISTENING: "LISTENING",
    FAILED: "FAILED",
    SCHEDULED: "SCHEDULED",
    PAUSED: "PAUSED",
    WAITING: "WAITING",
    ARCHIVED: "ARCHIVED",
};

const CampaignStatusToVariantMap = {
    [CampaignStatus.CREATING]: "primary",
    [CampaignStatus.RUNNING]: "secondary",
    [CampaignStatus.FINISHED]: "success",
    [CampaignStatus.LISTENING]: "secondary",
    [CampaignStatus.FAILED]: "danger",
    [CampaignStatus.SCHEDULED]: "warning",
    [CampaignStatus.PAUSED]: "orange",
    [CampaignStatus.WAITING]: "warning",
    [CampaignStatus.ARCHIVED]: "danger",
};

const CampaignsFilterOptions = {
    STATUS: "STATUS",
    TO_SMS: "TO_SMS",
    TO_EMAIL: "TO_EMAIL",
};

const CampaignStatusOptions = Object.keys(CampaignStatus).map((status) => ({
    label: toTitleCase(status),
    value: status,
}));

const LaunchDates = {
    NOW: "NOW",
    LATER: "LATER",
};

const ObjectKeyMap = {
    SMS: "smsHeaders",
    EMAIL: "emailHeaders",
    BANNER: "bannerHeaders",
};

const CampaignDetailsViewMainHeader = {
    SMS: "SMS Campaigns Details",
    EMAIL: "Email Campaigns Details",
    BANNER: "Banner Campaigns Details",
};

const CampaignDetailsSectionTwo = {
    emailHeaders: {
        totalMessages: {
            label: "Total Messages",
            nextPointArrow: true,
            isFirstStep: true,
        },
        successCount: {
            label: "Submitted Messages",
            nextPointArrow: true,
            percentageKey: "submittedPercentage",
            extraRowKey: "failedToSubmit",
        },
        deliveredMessages: {
            label: "Delivered Messages",
            nextPointArrow: true,
            percentageKey: "openedPercentage",
            extraRowKey: "bounce",
        },
        openedMessages: {
            label: "Opened Messages",
            nextPointArrow: true,
            percentageKey: "clickedPercentage",
            extraRowKey: "spam",
        },
        clickedMessages: { label: "Clicked Messages", nextPointArrow: false },
    },
    smsHeaders: {
        totalMessages: {
            label: "Total Messages",
            nextPointArrow: true,
            isFirstStep: true,
        },
        successCount: {
            label: "Submitted Messages",
            nextPointArrow: false,
            percentageKey: "submittedPercentage",
            extraRowKey: "failedToSubmit",
        },
    },
    bannerHeaders: {
        totalMessages: {
            label: "Total Banners",
            nextPointArrow: true,
            isFirstStep: true,
        },
        successCount: {
            label: "Active Banners",
            nextPointArrow: false,
            percentageKey: "activePercentage",
            extraRowKey: "inactiveBanners",
        },
    },
};

const CampaignDetailsSectionTwoColumnSize = {
    EMAIL: "flex-container-div-col-2",
    SMS: "flex-container-div-col-3",
    BANNER: "flex-container-div-col-3",
};

const MarketingConsentFilter = {
    id: "notificationPreference.allowPromotionalNotifications",
    type: "group",
    disabled: true,
    children1: {
        "bab88ba9-0123-4456-b89a-b193d04e05ee": {
            type: "rule",
            properties: {
                field: "notificationPreference.allowPromotionalNotifications",
                operator: "equal",
                value: ["true"],
                valueSrc: ["value"],
                valueError: [null],
                valueType: ["text"],
            },
        },
    },
};

const PromotionalCampaignFilter = {
    id: "marketing_consent_filter",
    segment: {
        id: "custom_filter",
        name: "Marketing/Promotional Consented Members",
        isCustomFilter: true,
    },
    memberFilter: MarketingConsentFilter,
};

const PromotionalCampaignMsg = (
    <>
        {"Please note that all "}
        <span className="font-weight-bold">
            {`${toTitleCase(CampaignTypes.PROMOTIONAL)} Campaigns`}
        </span>
        {" will "}
        <span className="font-weight-bold">ONLY</span>
        {" be sent to "}
        <span className="font-weight-bold">Loyalty Members</span>
        {
            " who have given their consent to receive marketing and/or promotional material, regardless of any selected segments or applied filters."
        }
    </>
);

export {
    CampaignChannels,
    CampaignStatus,
    CampaignsFilterOptions,
    CampaignStatusOptions,
    LaunchDates,
    ObjectKeyMap,
    CampaignDetailsViewMainHeader,
    CampaignDetailsSectionTwo,
    CampaignDetailsSectionTwoColumnSize,
    CampaignTypes,
    CampaignSubTypes,
    CampaignSubTypesInfo,
    CampaignTypeOptions,
    MarketingConsentFilter,
    PromotionalCampaignFilter,
    PromotionalCampaignMsg,
    CampaignStatusToVariantMap,
    CampaignAction,
    GoalDrivenCampaignsStatus,
    GoalDrivenCampaigns,
    CampaignSubTypesTabs,
};
